<template>
    <stat-card :expandable="expandable" :expanded="expanded" @toggle:expand="$emit('toggle:expand')">
        <template #header>
            <kimsa-combobox v-if="chartType === 'bar'" class="wc-metric" dense solo :items="variableOptions"
                :value="selectedVariable" @change="selectedVariable = $event" placeholder-class no-object height="30" />
            <span v-else />
            <segmented-tabs class="wc-views" :options="viewOptions" :value="chartType" @change="chartType = $event" />
        </template>

        <div v-if="noData" class="wc-empty">
            <v-icon size="34" color="txtMuted">mdi-magnify-close</v-icon>
            <span>{{ $t('common.without_data') }}</span>
        </div>

        <template v-else-if="chartType === 'bar'">
            <div class="wc-toolbar">
                <div v-if="showLegend" class="wc-pills">
                    <span v-for="v in variableLegend" :key="v.key" class="wc-pill"
                        :style="{ backgroundColor: v.color, color: textColor(v.color) }">{{ v.label }}</span>
                </div>
                <div v-else />
                <group-by-selector no-monthly @change="currentGroupBy = $event" :value="currentGroupBy" />
            </div>
            <bar-chart :labels="labels" :datasets="datasets" :lowPerformanceDays="lowPerformanceDays"
                :styles="chartStyles" />
        </template>

        <template v-else>
            <radar-chart :labels="radarLabels" :datasets="radarDatasets" :max="6" clean :styles="chartStyles" />
            <div class="wc-radar-legend">
                <span class="wc-radar-date">
                    <span class="wc-radar-dot" :style="{ background: radarColors.last }" />{{ $t('statistics.last_value') }}
                </span>
                <span class="wc-radar-date">
                    <span class="wc-radar-dot" :style="{ background: radarColors.avg }" />{{ $t('statistics.avg_last_7') }}
                </span>
            </div>
        </template>
    </stat-card>
</template>

<script>
import GroupBySelector from "@/components/statistics/GroupBySelector";
import BarChart from "@/components/common/BarChart";
import RadarChart from "@/components/common/RadarChart";
import _groupBy from 'lodash/groupBy'
import KimsaCombobox from '../common/inputs/KimsaCombobox.vue';
import StatCard from './StatCard.vue';
import { expandableCardMixin } from '@/mixins/expandableCard';
import SegmentedTabs from '../common/SegmentedTabs.vue';
import { sumWellnessPreInfo, wellnessColors } from "../../utils/Routine";

export default {
    name: "WellnessChart",
    components: { BarChart, GroupBySelector, RadarChart, KimsaCombobox, StatCard, SegmentedTabs },
    mixins: [expandableCardMixin],
    props: {
        docs: {
            type: Array,
            default: null,
        },
        compareDocs: {
            type: Array,
            default: null,
        },
        compareLabel: {
            type: String,
            default: null,
        }
    },
    data(vm) {
        return {
            currentGroupBy: 'daily',
            selectedVariable: 'all',
            chartType: 'bar',
            datasets: [],
            labels: [],
            colors: [
                wellnessColors.sleep_night,
                wellnessColors.muscles_hurt,
                wellnessColors.tired,
                wellnessColors.stressed,
                wellnessColors.energy,
            ],
            radarColors: {
                last: '#CCFF33',
                avg: '#66CCCC',
                low: '#E85C5C',
            },
            chartStyles: { position: 'relative', height: '220px' },
            viewOptions: [
                { name: vm.$t('statistics.bar_chart'), value: 'bar' },
                { name: vm.$t('statistics.radar_chart'), value: 'radar' },
            ],
            lowPerformanceDays: [],
        }
    },
    computed: {
        hasCompare(vm = this) {
            return !!(vm.compareDocs && vm.compareDocs.length)
        },
        compareTitle(vm = this) {
            return vm.compareLabel || vm.$t('common.team')
        },
        noData(vm = this) {
            return vm.docs.filter(doc => doc?.advance?.pre).length === 0
        },
        showLegend(vm = this) {
            return vm.selectedVariable === 'all'
        },
        variableLegend(vm = this) {
            return [
                { key: 'tired', label: vm.$t('statistics.tired'), color: vm.colors[2] },
                { key: 'energy', label: vm.$t('statistics.energy'), color: vm.colors[4] },
                { key: 'stressed', label: vm.$t('statistics.stressed'), color: vm.colors[3] },
                { key: 'muscles_hurt', label: vm.$t('statistics.muscles_hurt'), color: vm.colors[1] },
                { key: 'sleep_night', label: vm.$t('statistics.sleep_night'), color: vm.colors[0] },
            ]
        },
        variableLabels(vm = this) {
            return {
                sleep_night: vm.$t('statistics.sleep_night'),
                muscles_hurt: vm.$t('statistics.muscles_hurt'),
                tired: vm.$t('statistics.tired'),
                stressed: vm.$t('statistics.stressed'),
                energy: vm.$t('statistics.energy'),
            }
        },
        variableOptions(vm = this) {
            return [
                { name: vm.$t('statistics.wellness'), value: 'all' },
                { name: vm.$t('statistics.energy'), value: 'energy' },
                { name: vm.$t('statistics.tired'), value: 'tired' },
                { name: vm.$t('statistics.stressed'), value: 'stressed' },
                { name: vm.$t('statistics.muscles_hurt'), value: 'muscles_hurt' },
                { name: vm.$t('statistics.sleep_night'), value: 'sleep_night' },
            ]
        },
        selectedVariableLabel(vm = this) {
            return vm.variableOptions.find(o => o.value === vm.selectedVariable)?.name || ''
        },
        titleLabel(vm = this) {
            return vm.chartType === 'radar' ? vm.$t('statistics.wellness') : vm.selectedVariableLabel
        },
        radarSourceDocs(vm = this) {
            return (vm.docs || []).filter(doc => doc?.advance?.pre)
        },
        radarSortedDocs(vm = this) {
            return [...vm.radarSourceDocs].sort((a, b) =>
                vm.$moment(b.rawDate.full).toDate() - vm.$moment(a.rawDate.full).toDate()
            )
        },
        radarLabels(vm = this) {
            return [
                vm.$t('statistics.sleep_night'),
                vm.$t('statistics.muscles_hurt'),
                vm.$t('statistics.tired'),
                vm.$t('statistics.stressed'),
                vm.$t('statistics.energy'),
            ]
        },
        radarDatasets(vm = this) {
            const sorted = vm.radarSortedDocs
            if (sorted.length === 0) return []
            const variables = ['sleep_night', 'muscles_hurt', 'tired', 'stressed', 'energy']
            const round1 = n => Number(n.toFixed(1))

            const lastRoutine = sorted[0]
            const lastValues = variables.map(v => Number(lastRoutine?.advance?.preInfo?.[v] || 1))

            const avgSource = sorted.slice(0, 7)
            const avgValues = variables.map(v => {
                const sum = avgSource.reduce((s, r) => s + Number(r?.advance?.preInfo?.[v] || 1), 0)
                return round1(sum / avgSource.length)
            })

            const pointColors = (values) => {
                const minVal = Math.min.apply(null, values)
                return values.map(val => val === minVal ? vm.radarColors.low : null)
            }

            const lastPointColors = pointColors(lastValues).map(c => c || vm.radarColors.last)
            const avgPointColors = pointColors(avgValues).map(c => c || vm.radarColors.avg)

            return [
                {
                    label: vm.$t('statistics.last_value'),
                    backgroundColor: 'rgba(204, 255, 51, 0.12)',
                    borderColor: vm.radarColors.last,
                    pointBackgroundColor: lastPointColors,
                    pointBorderColor: lastPointColors,
                    pointHoverBackgroundColor: lastPointColors,
                    pointHoverBorderColor: lastPointColors,
                    pointRadius: 4,
                    pointHoverRadius: 5,
                    data: lastValues,
                    dataReal: lastValues,
                },
                {
                    label: vm.$t('statistics.avg_last_7'),
                    backgroundColor: 'rgba(102, 204, 204, 0.08)',
                    borderColor: vm.radarColors.avg,
                    borderDash: [4, 3],
                    pointBackgroundColor: avgPointColors,
                    pointBorderColor: avgPointColors,
                    pointHoverBackgroundColor: avgPointColors,
                    pointHoverBorderColor: avgPointColors,
                    pointRadius: 4,
                    pointHoverRadius: 5,
                    borderWidth: 2,
                    data: avgValues,
                    dataReal: avgValues,
                },
            ]
        },
    },
    mounted() {
        const vm = this
        vm.setCharts()
    },
    methods: {
        textColor(hex) {
            const h = (hex || '').replace('#', '')
            if (h.length !== 6) return '#FFFFFF'
            const r = parseInt(h.slice(0, 2), 16)
            const g = parseInt(h.slice(2, 4), 16)
            const b = parseInt(h.slice(4, 6), 16)
            const luminance = 0.299 * r + 0.587 * g + 0.114 * b
            return luminance > 150 ? '#000000' : '#FFFFFF'
        },
        setCharts() {
            const vm = this;
            const { datasets, labels } = vm.loadStats(vm.docsGrouped(vm.docs || []))

            vm.datasets = datasets
            vm.labels = labels
            vm.lowPerformanceDays = vm.hasCompare ? vm.findLowPerformanceDays(datasets, labels) : []
        },
        findLowPerformanceDays(datasets, labels) {
            const vm = this
            const compare = vm.loadStats(vm.docsGrouped(vm.compareDocs))
            const compareDays = compare.datasets.find(dataset => dataset.days)?.days || []
            const mainDays = datasets.find(dataset => dataset.days)?.days || []

            return mainDays.filter((day, index) => {
                const compareIndex = compareDays.indexOf(day)
                return compareIndex !== -1 && labels[index] < compare.labels[compareIndex]
            })
        },
        docsGrouped(docs) {
            const vm = this;
            let currentDocs = docs.filter(doc => doc?.advance?.pre).map(doc => {
                let date = vm.$moment(doc.rawDate.full)
                if (vm.currentGroupBy === 'weekly') date = date.startOf('week')
                let routineNumber = doc.routineNumber === 2 ? ' E2' : ''
                return {
                    date: date,
                    dateString: date.format('DD MMM').replace('.', '') + routineNumber,
                    sleep_night: Number(doc?.advance?.preInfo?.sleep_night || 1),
                    muscles_hurt: Number(doc?.advance?.preInfo?.muscles_hurt || 1),
                    tired: Number(doc?.advance?.preInfo?.tired || 1),
                    stressed: Number(doc?.advance?.preInfo?.stressed || 1),
                    energy: Number(doc?.advance?.preInfo?.energy || 1),
                }
            })

            if (vm.currentGroupBy !== 'daily') {
                let grouped = _groupBy(currentDocs, d => {
                    return d.dateString
                })

                const round1 = n => Number(n.toFixed(1))

                currentDocs = []
                for (let currentDate in grouped) {
                    const items = grouped[currentDate]
                    if (items.length === 1) {
                        currentDocs.push(items[0])
                    } else {
                        const n = items.length
                        const avg = key => round1(items.reduce((s, x) => s + x[key], 0) / n)
                        currentDocs.push({
                            date: items[0].date,
                            dateString: items[0].dateString,
                            sleep_night: avg('sleep_night'),
                            muscles_hurt: avg('muscles_hurt'),
                            tired: avg('tired'),
                            stressed: avg('stressed'),
                            energy: avg('energy'),
                        })
                    }
                }
            }

            return currentDocs
        },
        loadStats(docsGrouped) {
            const vm = this

            let labels = []
            let sleep_night = []
            let muscles_hurt = []
            let tired = []
            let stressed = []
            let energy = []
            let days = []

            const round1 = n => Number(n.toFixed(1))

            docsGrouped.forEach(doc => {
                labels.push(round1(sumWellnessPreInfo(doc)))
                stressed.push(doc.stressed)
                tired.push(doc.tired)
                muscles_hurt.push(doc.muscles_hurt)
                sleep_night.push(doc.sleep_night)
                energy.push(doc.energy)
                days.push(doc.dateString)
            })

            const barPercentage = vm.currentGroupBy === 'weekly' ? 0.4 : 0.2

            const allDatasets = [
                { key: 'sleep_night', data: sleep_night, label: vm.variableLabels.sleep_night, backgroundColor: vm.colors[0], barPercentage },
                { key: 'muscles_hurt', data: muscles_hurt, label: vm.variableLabels.muscles_hurt, backgroundColor: vm.colors[1], barPercentage },
                { key: 'stressed', data: stressed, label: vm.variableLabels.stressed, backgroundColor: vm.colors[3], barPercentage },
                { key: 'energy', data: energy, label: vm.variableLabels.energy, backgroundColor: vm.colors[4], barPercentage },
                { key: 'tired', data: tired, label: vm.variableLabels.tired, backgroundColor: vm.colors[2], days: days, barPercentage },
            ]

            if (vm.selectedVariable !== 'all') {
                const single = allDatasets.find(ds => ds.key === vm.selectedVariable)
                const selectedDataset = { ...single, days }
                return {
                    labels: [...selectedDataset.data],
                    datasets: [selectedDataset],
                }
            }

            return { labels, datasets: allDatasets }
        }
    },
    watch: {
        docs() {
            this.setCharts()
        },
        compareDocs() {
            this.setCharts()
        },
        currentGroupBy() {
            this.setCharts()
        },
        selectedVariable() {
            this.setCharts()
        }
    }
}
</script>

<style scoped>
.wc-metric {
    flex: 1;
    max-width: 200px;
}

.wc-metric ::v-deep .v-input,
.wc-metric ::v-deep .v-text-field {
    margin: 0;
    padding: 0;
}

.wc-metric ::v-deep .v-input__control,
.wc-metric ::v-deep .v-input__slot {
    min-height: 30px !important;
}

.wc-metric ::v-deep .v-input__slot {
    padding: 0 10px !important;
}

.wc-metric ::v-deep .v-select__selection {
    font-size: 12px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: normal;
    color: var(--v-txtSecondary-base);
}

.wc-metric ::v-deep .v-input__append-inner {
    margin-top: 0;
    align-self: center;
}

.wc-views {
    flex-shrink: 0;
    width: 140px;
}

.wc-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.wc-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
    flex-shrink: 0;
}

.wc-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
}

.wc-pill {
    padding: 3px 8px;
    border-radius: 10px;
    font-size: 9px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
}

.wc-radar-legend {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 8px;
    flex-shrink: 0;
}

.wc-radar-date {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
}

.wc-radar-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
}
</style>